import { AccountId, Timestamp, TokenId, UserId, Wei } from 'types'
import { http } from './http'

export enum ConvertOrderType {
  buy = 'buy',
  sell = 'sell',
}
export enum FlashOrderStatusCode {
  UserFlashOrderPlaced = 10,
  UserFlashOrderTimeoutProcessed = 11,
  MarketMakerFailedToFlash = 20,
  MarketMakerFlashFailuresProcessed = 21,
  FlashOrderReadyForLayer2Submission = 40,
  FlashOrderLayer2SubmissionRejected = 41,
  FlashOrderSentToLayer2 = 50,
  FlashOrderSuccessfulAtLayer2 = 60,
  FlashOrderFailedAtLayer2 = 70,
}

export type FlashOrderStatusMessage = {
  [key in FlashOrderStatusCode]: string
}
export const flashOrderStatusMessages: FlashOrderStatusMessage = {
  [FlashOrderStatusCode.UserFlashOrderPlaced]: 'Flash swap order placed',
  [FlashOrderStatusCode.UserFlashOrderTimeoutProcessed]:
    'Flash swap order timeout has been handled',
  [FlashOrderStatusCode.MarketMakerFailedToFlash]:
    'Market maker’s flash swap failed',
  [FlashOrderStatusCode.MarketMakerFlashFailuresProcessed]:
    'Market maker’s flash swap failure has been handled',
  [FlashOrderStatusCode.FlashOrderReadyForLayer2Submission]:
    'Flash swap order is ready, waiting for submission to layer2',
  [FlashOrderStatusCode.FlashOrderLayer2SubmissionRejected]:
    'Flash swap order was rejected due to failure in layer2 submission',
  [FlashOrderStatusCode.FlashOrderSentToLayer2]:
    'Flash swap order has been sent to layer2',
  [FlashOrderStatusCode.FlashOrderSuccessfulAtLayer2]:
    'Flash swap order successfully executed on layer2',
  [FlashOrderStatusCode.FlashOrderFailedAtLayer2]:
    'Flash swap order failed on layer2',
}
export interface GetConvertAskPriceParams {
  fromCurrency: string
  toCurrency: string
  fromAmount?: string | number
  toAmount?: string | number
}

export interface PostFlashOrderParams {
  fromCurrency: string
  toCurrency: string
  direction: string
  price: string
  quantity: string
  slotId: number
  nonce: number
  signature: string
}

export interface ConvertOrderData {
  id: number
  l2baseTokenId: number
  l2quoteTokenId: number
  takerUserId: number
  takerL2userId: number
  takerSlot: number
  takerNonce: number
  takerFee: string
  takerFeeRatio: number
  makerUserId: number
  makerL2userId: number
  makerSlot: number
  makerNonce: number
  makerFee: string
  makerFeeRatio: number
  price: string
  size: string
  submitterFee: string
  submitterFeeToken: number
  submitterL2userId: number
  side: ConvertOrderType
  time: number
  l2swapTxid: string
  status: number
  failReason: string
  takerOrder: string
  makerOrder: string
  createdAt: number
  updatedAt: number
}

export interface GetConvertAskPriceData {
  expireTime: number
  price: string
  priceFrom: Wei
  priceTo: Wei
}

export interface GetConvertAskPrice {
  (
    params: GetConvertAskPriceParams,
    options?: any,
  ): Promise<GetConvertAskPriceData>
}
export const getConvertAskPrice: GetConvertAskPrice = async (params) => {
  const r = await http
    .get(`/convert/askPrice`, {
      params,
    })
    .then((r) => r.data)
  return r.data
}

export interface PostFlashOrder {
  (
    accountId: number,
    params: PostFlashOrderParams,
    options?: any,
  ): Promise<ConvertOrderData>
}
export const postFlashOrder: PostFlashOrder = (accountId, params) => {
  return http.post(`/convert/${accountId}/placeOrder`, params)
}

export const getConvertFeeRate = async (
  from: string,
  to: string,
): Promise<{ feeRate: number }> => {
  const r = await http
    .get(`/convert/feeRate?fromCurrency=${from}&toCurrency=${to}`)
    .then((r) => r.data)
  return r.data
}

export interface ConvertHistoryParams {
  days?: string
  currency?: string
  status?: ConvertHistoryItemStatus
  offsetId?: number
  limit?: number
}

export enum ConvertHistoryItemStatus {
  PENDING = 'PENDING',
  FILLED = 'FILLED',
  FAILED = 'FAILED',
}
export interface ConvertHistoryItem {
  id: number
  userId: UserId
  accountId: AccountId
  direction: string
  fromCurrencyId: TokenId
  toCurrencyId: TokenId
  status: ConvertHistoryItemStatus
  price: number
  quantity: number
  feeRate: number
  fee: number
  txHash: string
  createdAt: Timestamp
  updatedAt: Timestamp
  slotId: number
  nonce: number
  signature: string | null
}
export interface ConvertHistoryBody {
  hasMore: boolean
  nextOffsetId: number
  range: string
  results: ConvertHistoryItem[]
}
export const getConvertHistory = async (
  accountId: AccountId,
  params: ConvertHistoryParams,
): Promise<ConvertHistoryBody> => {
  const r = await http
    .get(`/convert/${accountId}/orders`, { params })
    .then((r) => r.data)
  return r.data
}
